import React, { useEffect, useState } from 'react';
import {
    Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText,
    DialogTitle, MenuItem, TextField, Typography,
} from '@mui/material';

const FOLLOW_LATEST = 'follow_latest';

export default function FactorDatasetSelector({
    catalogue, onLoad, disabled = false, busy = false, blockedReason = null, updateProgress = null,
}) {
    const data = catalogue?.data;
    const [selected, setSelected] = useState(FOLLOW_LATEST);
    // Load asks first, as a factor vendor switch does, because the date
    // re-checks the published Watchlist.
    const [confirming, setConfirming] = useState(false);
    useEffect(() => {
        setSelected(data?.selection_mode === 'pinned' && data?.current?.release_id
            ? String(data.current.release_id) : FOLLOW_LATEST);
        setConfirming(false);
    }, [data?.current_revision_id, data?.selection_mode, data?.current?.release_id]);
    if (!catalogue?.enabled) return null;
    if (!data) {
        return <Box sx={{ display: 'grid', gap: 1.5 }}>
            {catalogue.loading
                ? <Box role="status" sx={{ display: 'flex', gap: 1, alignItems: 'center' }}><CircularProgress size={20} />Checking available factor dates…</Box>
                : <Alert severity={catalogue.error ? 'warning' : 'info'}>{catalogue.error || 'Available factor dates have not loaded. Refresh to try again.'}</Alert>}
            <Box sx={{ display: 'flex', gap: 1 }}>
                <Button variant="contained" disabled>Load</Button>
                <Button onClick={catalogue.refresh} disabled={catalogue.loading || disabled || busy}>Refresh dates</Button>
            </Box>
        </Box>;
    }

    const items = data.items || [];
    const current = data?.current;
    const selectedItem = items.find(item => String(item.release_id) === selected);
    const mode = selected === FOLLOW_LATEST ? FOLLOW_LATEST : 'pinned';
    const active = data.available || items.find(item => item.is_active);
    const unchanged = mode === data.selection_mode
        && (mode === FOLLOW_LATEST
            ? active?.release_id === current?.release_id
            : selected === String(current?.release_id));
    const unavailable = mode === 'pinned' && !selectedItem;
    const locked = disabled || busy;
    const loadDisabled = locked || unchanged || unavailable || !data.current_revision_id || Boolean(catalogue.error);
    const targetDate = mode === FOLLOW_LATEST ? active?.as_of_date : selectedItem?.as_of_date;
    const load = () => {
        setConfirming(false);
        onLoad?.({
            selectionMode: mode,
            releaseId: selectedItem?.release_id,
            expectedRevisionId: data.current_revision_id,
        });
    };

    return (
        <Box sx={{ display: 'grid', gap: 1.5 }}>
            <Typography variant="body2">
                Loaded date: {current?.as_of_date || 'No factor dataset loaded'}
                {data.selection_mode === 'pinned' ? ' · Keeping this dataset' : ''}
            </Typography>
            <TextField
                select fullWidth size="small" label="Factor data date" value={selected}
                onChange={event => setSelected(event.target.value)} disabled={locked}
                helperText={locked ? blockedReason
                    || 'Loading factor data and checking your Watchlist. Date selection unlocks as soon as the updated app data is ready.' : undefined}
                FormHelperTextProps={{ role: locked ? 'status' : undefined }}
            >
                <MenuItem value={FOLLOW_LATEST}>
                    Follow available updates{active?.as_of_date ? ` · ${active.as_of_date}` : ''}
                </MenuItem>
                {items.map(item => (
                    <MenuItem key={item.release_id} value={String(item.release_id)}>
                        {item.as_of_date}{item.is_active ? ' · Recommended' : ''}
                    </MenuItem>
                ))}
                {unavailable && (
                    <MenuItem value={selected} disabled>
                        {current?.release_id && selected === String(current.release_id)
                            ? `${current.as_of_date} · Loaded` : 'Previously selected date · Refresh required'}
                    </MenuItem>
                )}
            </TextField>
            {locked && updateProgress && <Box>{updateProgress}</Box>}
            <Typography variant="body2" color="text.secondary">
                Loading re-checks your Watchlist. You can review securities that would be added, removed,
                or assigned a different country before those changes are published.
                {' '}Choosing a date keeps that dataset until you choose another or follow available updates.
            </Typography>
            {mode === FOLLOW_LATEST && (
                <Typography variant="caption" color="text.secondary">
                    Future updates use your Factor data auto-sync preference. Loading now approves a re-check against the available dataset.
                </Typography>
            )}
            {!data.current_revision_id && (
                <Alert severity="info">Publish a Watchlist before choosing a factor data date.</Alert>
            )}
            {catalogue.error && <Alert severity="warning">{catalogue.error}</Alert>}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button variant="contained" onClick={() => setConfirming(true)}
                    disabled={loadDisabled}
                    startIcon={busy ? <CircularProgress size={14} color="inherit" /> : null}>
                    {busy ? 'Checking your Watchlist…' : 'Load'}
                </Button>
                <Button onClick={catalogue.refresh} disabled={catalogue.loading || locked}>Refresh dates</Button>
                {data.next_before_date && (
                    <Button onClick={catalogue.loadMore} disabled={catalogue.loading || locked}>Earlier dates</Button>
                )}
                {catalogue.loading && <CircularProgress size={20} aria-label="Checking available factor dates" />}
            </Box>
            <Dialog
                open={confirming}
                onClose={() => setConfirming(false)}
                aria-labelledby="factor-dataset-load-title"
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle id="factor-dataset-load-title">
                    {mode === FOLLOW_LATEST ? 'Follow available updates?' : `Load ${targetDate} factor data?`}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {`Your Watchlist will be re-checked against ${targetDate ? `the ${targetDate}` : 'the available'} `
                            + 'factor data and switches when the check completes. If any securities change, '
                            + 'you review them first.'}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirming(false)}>Keep current data</Button>
                    <Button variant="contained" onClick={load} disabled={loadDisabled}>
                        {mode === FOLLOW_LATEST ? 'Follow updates' : `Load ${targetDate}`}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
